import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../components/Utils/ModalShow';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { apiService } from '../../../components/Config/api.service';
import { notify } from '../../../components/Utils/Notifications';
import PayslipStateBadge from './PayslipStateBadge';
import { FaPrint, FaPaperPlane, FaFileInvoiceDollar } from 'react-icons/fa';

// Utilitaire pour convertir un blob en base64
const pdfBlobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

const PayslipModal = ({ isOpen, onClose, employee, month, year, onUpdate }) => {
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
    const [payslip, setPayslip] = useState(null);
    const [pdfUrl, setPdfUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [manualCc, setManualCc] = useState('');

    const selectedMonth = `${year}-${String(month).padStart(2, '0')}`;
    const employeeId = employee?.employee_id;

    const fetchPayslipDetails = useCallback(async () => {
        if (!employeeId || !selectedMonth) return;
        setIsLoading(true);
        setPdfUrl('');
        setPayslip(null);
        try {
            const response = await apiService.getElement(`/payslip/employee/${employeeId}/month/${selectedMonth}`);
            if (response.data.found) {
                const fetchedPayslip = response.data.payslip;
                setPayslip(fetchedPayslip);
                const pdfResp = await apiService.getBlobFile(`/payslip/pdf/${fetchedPayslip.id}`);
                const url = window.URL.createObjectURL(new Blob([pdfResp.data], { type: 'application/pdf' }));
                setPdfUrl(url);
            }
        } catch (error) {
            console.error("Erreur lors de la récupération du bulletin", error);
            notify(t('payslipModal.fetchError'), null, 3);
        } finally {
            setIsLoading(false);
        }
    }, [employeeId, selectedMonth]);

    useEffect(() => {
        if (isOpen) {
            fetchPayslipDetails();
        }
    }, [isOpen, fetchPayslipDetails]);

    useEffect(() => {
        if (!payslip) {
            setManualCc('');
            return;
        }
        setManualCc((payslip.email_cc || payslip.cc_email || '').trim());
    }, [payslip]);

    const handlePrint = async () => {
        if (!pdfUrl) return;
        setIsPrinting(true);
        try {
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.setAttribute('download', `bulletin-${payslip.Employee}-${selectedMonth}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            notify(t('payslipModal.downloadSuccess'), null, 2);
        } catch (e) {
            notify(t('payslipModal.downloadError'), null, 3);
        } finally {
            setIsPrinting(false);
        }
    };

    const monthName = new Date(year, month - 1).toLocaleDateString(dateLocale, { month: 'long' });

    const handleSend = async () => {
        if (!payslip || !payslip.email) {
            notify(t('payslipModal.missingEmail'), null, 4);
            return;
        }
        if (!pdfUrl) {
            notify(t('payslipModal.pdfNotAvailable'), null, 4);
            return;
        }
        setIsSending(true);
        try {
            const pdfResp = await apiService.getBlobFile(`/payslip/pdf/${payslip.id}`);
            const blob = new Blob([pdfResp.data], { type: 'application/pdf' });
            const base64data = await pdfBlobToBase64(blob);
            await apiService.sendEmail({
                to: payslip.email,
                cc: manualCc.trim() || undefined,
                subject: `Votre bulletin de paie - ${monthName} ${year}`,
                html: `<p>Bonjour ${payslip.Employee || ''},</p><p>Veuillez trouver votre bulletin de paie pour le mois de ${monthName} ${year} en pièce jointe.</p><p>Cordialement,</p>`,
                template_id: 1,
                params: {
                    prenom: payslip.Employee || "l'employé",
                    month: `${monthName} ${year}`,
                },
                attachment: {
                    filename: `bulletin-${payslip.Employee}-${selectedMonth}.pdf`,
                    content: base64data,
                },
            });

            notify(t('payslipModal.sendSuccess'), null, 2);
        } catch (e) {
            console.error("Erreur lors de l'envoi du bulletin:", e);
            notify(t('payslipModal.sendError'), null, 3);
        } finally {
            setIsSending(false);
        }
    };

    const renderFooter = () => (
        <div className="flex items-center justify-between w-full gap-3">
            <div>
                {payslip && <PayslipStateBadge state={payslip.State} />}
            </div>
            <div className="flex items-center gap-3">
                <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium">{t('payslipModal.close')}</button>
                {payslip && (
                    <>
                        <button
                            onClick={handleSend}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 border border-blue-300 hover:bg-blue-200 rounded-lg font-medium"
                            disabled={isLoading || isSending || !payslip.email}
                            title={!payslip.email ? t('payslipModal.emailNotAvailable') : t('payslipModal.sendByEmail')}
                        >
                            {isSending ? (
                                <span className="loading loading-spinner loading-xs"></span>
                            ) : (
                                <FaPaperPlane />
                            )}
                            {isSending ? t('payslipModal.sending') : t('payslipModal.send')}
                        </button>
                        <button
                            onClick={handlePrint}
                            className="flex items-center gap-2 px-4 py-2 bg-[--color-primary] text-white rounded-lg font-medium"
                            disabled={!pdfUrl || isLoading || isPrinting}
                        >
                            {isPrinting ? (
                                <span className="loading loading-spinner loading-xs"></span>
                            ) : (
                                <FaPrint />
                            )}
                            {isPrinting ? t('payslipModal.downloading') : t('payslipModal.print')}
                        </button>
                    </>
                )}
            </div>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t('payslipModal.payslipOf', { employee: employee?.employee_name || '' })}
            subtitle={`${monthName} ${year}`}
            size={900}
            footer={renderFooter()}
        >
            <div className="p-6 min-h-[60vh] max-h-[75vh] flex flex-col">
                {payslip && (
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('payslipConfirmModal.ccLabel')}
                        </label>
                        <input
                            type="text"
                            value={manualCc}
                            onChange={(e) => setManualCc(e.target.value)}
                            placeholder="manager@yros.io, rh@yros.io"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[--color-primary]"
                        />
                    </div>
                )}
                {isLoading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="loading loading-spinner text-primary text-lg"></div>
                    </div>
                ) : pdfUrl ? (
                    <div className="flex-1 overflow-y-auto">
                        <Worker workerUrl={`${process.env.PUBLIC_URL}/pdf.worker.min.js`}>
                            <Viewer fileUrl={pdfUrl} />
                        </Worker>
                    </div>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-500">
                        <p>{t('payslipModal.noPayslipFound')}</p>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default PayslipModal;
